import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Navigate, Route, Routes, useLocation, useParams } from "react-router";
import { useApp } from "./app-context.tsx";
import { useAuthUser } from "./auth.ts";
import { localeOptions as brandLocaleOptions } from "./locale.ts";
import { InlineMarkdown } from "./markdown.tsx";
import { useMe } from "./me.ts";
import { LegalPage } from "./pages/Legal.tsx";
import { CatalogPage } from "./pages/Catalog.tsx";
import { ConfiguratorPage } from "./pages/Configurator.tsx";
import { MinePage } from "./pages/Mine.tsx";
import { ModerationPage } from "./pages/Moderation.tsx";
import { SharePage } from "./pages/Share.tsx";
import { DefaultFooter, DefaultHeader } from "./pages/defaults.tsx";

/** Old `/community/<id>` links: open the entry's design with it applied (`?community=<id>`). */
function LegacyCommunityItem() {
  const { id = "" } = useParams();
  const { api } = useApp();
  const q = useQuery({ queryKey: ["public", id], queryFn: () => api!.getPublic(id), enabled: !!api, retry: false });
  if (api && q.isLoading) return null;
  return <Navigate to={q.data ? `/design/${encodeURIComponent(q.data.designId)}?community=${encodeURIComponent(id)}` : "/"} replace />;
}

export function App() {
  const { brand, copy, auth, locale, setLocale } = useApp();
  const localeOptions = useMemo(() => brandLocaleOptions(brand.config), [brand.config]);
  const user = useAuthUser(auth);
  const location = useLocation();
  const me = useMe();
  const gallery = brand.config.features.publicGallery;
  const Header = brand.slots?.Header ?? DefaultHeader;
  const Footer = brand.slots?.Footer ?? DefaultFooter;
  const nav = [
    { label: copy("nav.catalog"), href: "/", active: location.pathname === "/" || location.pathname.startsWith("/design") },
    ...(brand.config.features.saveConfigs ? [{ label: copy("nav.mine"), href: "/mine", active: location.pathname.startsWith("/mine") }] : []),
    ...(gallery && me.data?.plan === "staff" ? [{ label: copy("mod.title"), href: "/mod", active: location.pathname.startsWith("/mod") }] : []),
  ];
  const legal = brand.config.legal;
  const footerLinks = [
    ...(legal?.privacy ? [{ label: copy("footer.privacy"), href: "/privacy" }] : []),
    ...(legal?.terms ? [{ label: copy("footer.terms"), href: "/terms" }] : []),
    ...(legal?.contactEmail ? [{ label: copy("footer.contact"), href: `mailto:${legal.contactEmail}` }] : []),
  ];
  return (
    <div className="app-shell" data-brand={brand.config.id}>
      <Header
        brand={brand.config}
        nav={nav}
        user={user}
        onSignIn={() => void auth.signIn()}
        onSignOut={() => void auth.signOut()}
        locale={{ current: locale, options: localeOptions, onChange: setLocale }}
      />
      <main className="app-main">
        <Routes>
          <Route path="/" element={<CatalogPage />} />
          <Route path="/design/:id" element={<ConfiguratorPage />} />
          <Route path="/mine" element={<MinePage />} />
          <Route path="/share/:token" element={<SharePage />} />
          {/* Community entries live next to each design now (the configurator's community tab). */}
          <Route path="/community" element={<Navigate to="/" replace />} />
          {gallery && <Route path="/community/:id" element={<LegacyCommunityItem />} />}
          {gallery && <Route path="/mod" element={<ModerationPage />} />}
          <Route path="/privacy" element={<LegalPage kind="privacy" />} />
          <Route path="/terms" element={<LegalPage kind="terms" />} />
          <Route path="/auth/callback" element={<Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <Footer
        brand={brand.config}
        madeWith={copy("footer.madeWith")}
        links={footerLinks}
        attribution={legal?.attribution ? <InlineMarkdown text={legal.attribution} /> : undefined}
      />
    </div>
  );
}
